import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FaUserCheck,
  FaUserTimes,
  FaCalendarCheck,
  FaClock,
  FaSearch,
  FaChevronDown,
  FaCheck,
  FaTimes,
} from "react-icons/fa";
import styles from "./AdminAttendancePanel.module.css";
import WebService from "../Services/WebService";
import classNames from "classnames";
import { toast } from "react-toastify";
import { formatAttendanceDate, formatAttendanceTime } from "./attendanceFormat";



// Table and Leave Card Types
interface AttendanceLog {
  attendance_id: number;
  username: string;
  employee_id: number;
  date: string;
  status: string;
  check_in_time: string | null;
  check_out_time: string | null;
  note?: string;
}
interface LeaveRequest {
  leave_id: number;
  leave_type: string;
  start_date: string;
  employee_name: string;
  user_id: number;
  end_date: string;
  reason: string;
  status: "APPROVED" | "PENDING" | "REJECTED";
  applied_at?: string;
  username?: string;
}

const PAGE_SIZE = 10;

function getMonthRange(ym: string): { from_date: string; to_date: string } {
  if (!ym) return { from_date: "", to_date: "" };
  const [year, month] = ym.split("-").map(Number);
  const from_date = `${year}-${String(month).padStart(2, "0")}-01`;
  const lastDay = new Date(year, month, 0).getDate();
  const to_date = `${year}-${String(month).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
  return { from_date, to_date };
}

const TABS = [
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

const AdminAttendancePanel: React.FC = () => {
  // Filters/state for attendance logs
  const [month, setMonth] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [logs, setLogs] = useState<AttendanceLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsLoadingMore, setLogsLoadingMore] = useState(false);
  const [logsPagination, setLogsPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    page_size: PAGE_SIZE,
  });
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  // Leave requests state
  const [leaveTab, setLeaveTab] = useState<"PENDING" | "APPROVED" | "REJECTED">(
    "PENDING"
  );
  const [todayStats, setTodayStats] = useState({
  present_today: 0,
  absent_today: 0,
  on_leave_today: 0,
  late_check_ins: 0,
});
const [statsLoading, setStatsLoading] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [leaveLoadingMore, setLeaveLoadingMore] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [leavePagination, setLeavePagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_records: 0,
    page_size: PAGE_SIZE,
  });
  const logsSentinelRef = useRef<HTMLDivElement | null>(null);
  const leaveSentinelRef = useRef<HTMLDivElement | null>(null);
  const logsFetchingRef = useRef(false);
  const leaveFetchingRef = useRef(false);
  const logsReqIdRef = useRef(0);
  const leaveReqIdRef = useRef(0);

  // Fetch Attendance logs
const fetchLogs = useCallback(async (
  page = 1,
  monthValue = month,
  searchValue = search,
  append = false
) => {
  if (append && logsFetchingRef.current) return;
  if (!append) logsReqIdRef.current += 1;
  const reqId = logsReqIdRef.current;
  logsFetchingRef.current = true;
  if (append) setLogsLoadingMore(true);
  else setLogsLoading(true);
  try {
    const params = [];
    // Compute dates if month selected
    if (monthValue) {
      const { from_date, to_date } = getMonthRange(monthValue);
      if (from_date) params.push(`from_date=${from_date}`);
      if (to_date) params.push(`to_date=${to_date}`);
    }
    if (searchValue) params.push(`user_like=${encodeURIComponent(searchValue)}`);
    params.push(`page_number=${page}`);
    params.push(`limit=${PAGE_SIZE}`);
    const query = params.length ? `?${params.join("&")}` : "";
    const res = await WebService.getAPI<any>({
      action: `/v1/door-management/attendance${query}`,
    });
    const payload = res?.data ?? res;
    const data = (payload?.attendance || payload?.logs || []).map((row: any) => {
      const rawStatus = row.status;
      let statusCode = "ABSENT";
      if (typeof rawStatus === "number") {
        statusCode = rawStatus === 1 ? "PRESENT" : "ABSENT";
      } else if (typeof rawStatus === "string" && rawStatus.trim()) {
        statusCode = rawStatus.toUpperCase().replace(/\s+/g, "_");
      }
      return {
        ...row,
        attendance_id: row.attendance_id ?? row.id,
        employee_id: row.employee_id ?? row.user_id,
        username: row.username || row.employee_name || "Employee",
        status: statusCode,
        status_label: row.status_label || statusCode.replace(/_/g, " "),
        check_in_time: row.check_in_time ?? row.check_in ?? null,
        check_out_time: row.check_out_time ?? row.check_out ?? null,
        date: row.date || row.attendance_date || "",
      } as AttendanceLog;
    });
    if (reqId !== logsReqIdRef.current) return;
    if (append) {
      setLogs((prev) => {
        const seen = new Set(prev.map((l) => l.attendance_id));
        return [...prev, ...data.filter((l: AttendanceLog) => !seen.has(l.attendance_id))];
      });
    } else {
      setLogs(data);
    }
    const pagination = payload?.pagination || {};
    setLogsPagination({
      current_page: pagination.current_page || page,
      total_pages: pagination.total_pages || 1,
      total_records: pagination.total_records || 0,
      page_size: pagination.page_size || PAGE_SIZE,
    });
  } catch {
    if (reqId !== logsReqIdRef.current) return;
    if (!append) setLogs([]);
  }
  if (reqId !== logsReqIdRef.current) return;
  setLogsLoading(false);
  setLogsLoadingMore(false);
  logsFetchingRef.current = false;
}, [month, search]);
  
  const fetchTodayStats = async () => {
  setStatsLoading(true);
  try {
    const res = await WebService.getAPI<any>({
      action: "/v1/door-management/attendance/dashboard",
    });
    const payload = res?.data ?? res;
    const today = payload?.today_data ?? payload ?? {};
    setTodayStats({
      present_today: Number(
        today.present_today ?? today.present_count ?? payload?.present_count ?? 0
      ),
      absent_today: Number(
        today.absent_today ?? today.absent_count ?? payload?.absent_count ?? 0
      ),
      on_leave_today: Number(
        today.on_leave_today ?? payload?.on_leave_today ?? 0
      ),
      late_check_ins: Number(
        today.late_check_ins ?? payload?.late_check_ins ?? 0
      ),
    });
  } catch {
    setTodayStats({
      present_today: 0,
      absent_today: 0,
      on_leave_today: 0,
      late_check_ins: 0,
    });
  }
  setStatsLoading(false);
};

  // Fetch Leave Requests
  const fetchLeaves = useCallback(async (
    status: "PENDING" | "APPROVED" | "REJECTED" = leaveTab,
    page = 1,
    append = false
  ) => {
    if (append && leaveFetchingRef.current) return;
    if (!append) leaveReqIdRef.current += 1;
    const reqId = leaveReqIdRef.current;
    leaveFetchingRef.current = true;
    if (append) setLeaveLoadingMore(true);
    else setLeaveLoading(true);
    try {
      const res = await WebService.getAPI<any>({
        action: `/v1/door-management/leave?status=${status}&page_number=${page}&limit=${PAGE_SIZE}`,
      });
      const payload = res?.data ?? res;
      const incoming: LeaveRequest[] = payload?.leaves || [];
      if (reqId !== leaveReqIdRef.current) return;
      if (append) {
        setLeaveRequests((prev) => {
          const seen = new Set(prev.map((l) => l.leave_id));
          return [...prev, ...incoming.filter((l) => !seen.has(l.leave_id))];
        });
      } else {
        setLeaveRequests(incoming);
      }
      const pagination = payload?.pagination || payload?.data?.pagination || {};
      setLeavePagination({
        current_page: pagination.current_page || page,
        total_pages: pagination.total_pages || 1,
        total_records: pagination.total_records || incoming.length,
        page_size: pagination.page_size || PAGE_SIZE,
      });
    } catch {
      if (reqId !== leaveReqIdRef.current) return;
      if (!append) setLeaveRequests([]);
    }
    if (reqId !== leaveReqIdRef.current) return;
    setLeaveLoading(false);
    setLeaveLoadingMore(false);
    leaveFetchingRef.current = false;
  }, [leaveTab]);

  // Approve/Reject Leave Handler
  const handleLeaveAction = async (leave_id: number, status: "APPROVED" | "REJECTED") => {
    setActionLoading(leave_id);
    try {
      await WebService.putAPI({
        action: `/v1/door-management/leave/${leave_id}/${status}`,
        body: {},
      });
      toast.success(status === "APPROVED" ? "Leave approved" : "Leave rejected");
      fetchLeaves(leaveTab, 1, false);
      fetchTodayStats();
    } catch (e: any) {
      const msg =
        typeof e === "string"
          ? e
          : e?.message || e?.data?.message || `Failed to ${status.toLowerCase()} leave`;
      toast.error(msg);
    }
    setActionLoading(null);
  };
  useEffect(() => {
    fetchTodayStats();
    fetchLogs(1, month, search, false);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchLeaves(leaveTab, 1, false);
    // eslint-disable-next-line
  }, [leaveTab]);

  const hasMoreLogs = logsPagination.current_page < logsPagination.total_pages;
  const hasMoreLeaves = leavePagination.current_page < leavePagination.total_pages;

  useEffect(() => {
    const node = logsSentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasMoreLogs &&
          !logsLoading &&
          !logsLoadingMore
        ) {
          fetchLogs(logsPagination.current_page + 1, month, search, true);
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    fetchLogs,
    hasMoreLogs,
    logsLoading,
    logsLoadingMore,
    logsPagination.current_page,
    month,
    search,
  ]);

  useEffect(() => {
    const node = leaveSentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasMoreLeaves &&
          !leaveLoading &&
          !leaveLoadingMore
        ) {
          fetchLeaves(leaveTab, leavePagination.current_page + 1, true);
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    fetchLeaves,
    hasMoreLeaves,
    leaveLoading,
    leaveLoadingMore,
    leavePagination.current_page,
    leaveTab,
  ]);

  // Formatters
  const formatTime = (t: string | null) => formatAttendanceTime(t);
  const formatDateShort = (t: string) => formatAttendanceDate(t);

  // Month options (simple last 12 months)
  const monthOptions = Array.from({ length: 12 }).map((_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    return {
      value: `${date.getFullYear()}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`,
      label: date.toLocaleString("default", { month: "long", year: "numeric" }),
    };
  });
  const statCards = [
  {
    title: "Present Today",
    value: statsLoading ? "--" : todayStats.present_today,
    icon: <FaUserCheck size={28} color="#28c76f" />,
    color: "#e5f7ed",
  },
  {
    title: "Absent Today",
    value: statsLoading ? "--" : todayStats.absent_today,
    icon: <FaUserTimes size={28} color="#ea5455" />,
    color: "#ffeaea",
  },
  {
    title: "On Leave",
    value: statsLoading ? "--" : todayStats.on_leave_today,
    icon: <FaCalendarCheck size={28} color="#ffc400" />,
    color: "#fff7e0",
  },
  {
    title: "Late Logins",
    value: statsLoading ? "--" : todayStats.late_check_ins,
    icon: <FaClock size={28} color="#ff9800" />,
    color: "#fbeee5",
  },
];

  // ========== RENDER ==========
  return (
    <div className={styles.pageWrapper}>
      {/* TOP: Stat Cards */}
      <div className={styles.statsRow}>
{statCards.map((stat, i) => (
  <div className={styles.statCard} style={{ background: stat.color }} key={stat.title}>
    <div className={styles.statIcon}>{stat.icon}</div>
    <div className={styles.statValue}>{stat.value}</div>
    <div className={styles.statLabel}>{stat.title}</div>

  </div>
))}

      </div>
      {/* MAIN GRID */}
      <div className={styles.mainGrid}>
        {/* LEFT: Today's Activity */}
        <div className={styles.leftCol}>
          <div className={styles.activityHeader}>
            <div className={styles.activityTitle}>Attendance Logs</div>
            <div className={styles.activityFilters}>
              <div className={styles.filterMonth}>
                <select
                  value={month}
                  onChange={(e) => {
                    setMonth(e.target.value);
                    fetchLogs(1, e.target.value, search, false);
                  }}
                >
                  <option value="">Select Month</option>
                  {monthOptions.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
                <FaChevronDown className={styles.selectArrow} />
              </div>
              <div className={styles.filterSearch}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search employees..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") fetchLogs(1, month, e.currentTarget.value, false);
                  }}
                  onBlur={() => fetchLogs(1, month, search, false)}
                />
              </div>
            </div>
          </div>
          {/* Table */}
          <div className={styles.activityTableWrap}>
            <table className={styles.activityTable}>
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Check-In</th>
                  <th>Check-Out</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {logsLoading && logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      Loading...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: "center" }}>
                      No records found
                    </td>
                  </tr>
                ) : (
                  logs.map((log, idx) => (
                    <tr key={log.attendance_id || `${log.username}-${log.date}-${idx}`}>
                      <td>
                        <div className={styles.empInfo}>
                          <div className={styles.empAvatar}>
                            <FaUserCheck size={22} />
                          </div>
                          <span>{log.username}</span>
                        </div>
                      </td>
                      <td>{formatTime(log.check_in_time)}</td>
                      <td>{formatTime(log.check_out_time)}</td>
                      <td>{formatDateShort(log.date)}</td>
                      <td>
                        <span
                          className={classNames(styles.statusChip, {
                            [styles.present]: log.status === "PRESENT",
                            [styles.absent]: log.status === "ABSENT",
                            [styles.leave]: log.status === "ON_LEAVE",
                            [styles.late]: log.status === "LATE",
                          })}
                        >
                          {(() => {
                            const label =
                              (log as any).status_label ||
                              String(log.status || "Absent");
                            const text = String(label);
                            return (
                              text.charAt(0).toUpperCase() +
                              text.slice(1).toLowerCase().replace(/_/g, " ")
                            );
                          })()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div
              ref={logsSentinelRef}
              className={styles.infiniteScrollSentinel}
              aria-hidden
            />
            {logsLoadingMore && (
              <div className={styles.infiniteScrollLoading}>Loading more...</div>
            )}
            {!logsLoading &&
              !logsLoadingMore &&
              !hasMoreLogs &&
              logs.length > 0 && (
                <div className={styles.infiniteScrollEnd}>End of logs</div>
              )}
          </div>
        </div>
        {/* RIGHT: Leave Requests */}
        <div className={styles.rightCol}>
          <div className={styles.leaveHeader}>Leave Requests</div>
          {/* Tabs */}
          <div className={styles.leaveTabs}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                className={classNames(styles.leaveTab, {
                  [styles.activeTab]: leaveTab === tab.key,
                })}
                onClick={() => setLeaveTab(tab.key as any)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          {/* Leave Cards */}
          <div className={styles.leaveCardsList}>
            {leaveLoading && leaveRequests.length === 0 ? (
              <div style={{ padding: 12, textAlign: "center" }}>Loading...</div>
            ) : leaveRequests.length === 0 ? (
              <div style={{ padding: 12, textAlign: "center" }}>
                No leave requests.
              </div>
            ) : (
              leaveRequests.map((req) => (
                <div className={styles.leaveCard} key={req.leave_id}>
                  <div className={styles.leaveCardHeader}>
                    <div className={styles.leaveCardAvatar}>
                      <FaUserCheck size={24} />
                    </div>
                    <div>
                     <div className={styles.leaveUser}>
  {req.employee_name || req.username || (req.user_id ? `User #${req.user_id}` : "Employee")}
</div>

                      <div className={styles.leaveDates}>
                        {formatDateShort(req.start_date)}
                        {" - "}
                        {formatDateShort(req.end_date)}
                      </div>
                      <div className={styles.leaveType}>{req.leave_type}</div>
                    </div>
                  </div>
                  {/* Approve/Reject Buttons (above comment) */}
                  {leaveTab === "PENDING" && (
                    <div className={styles.leaveActions}>
                      <button
                        className={classNames(styles.approveBtn)}
                        disabled={actionLoading === req.leave_id}
                        onClick={() => handleLeaveAction(req.leave_id, "APPROVED")}
                      >
                        <FaCheck />
                        Approve
                      </button>
                      <button
                        className={classNames(styles.rejectBtn)}
                        disabled={actionLoading === req.leave_id}
                        onClick={() => handleLeaveAction(req.leave_id, "REJECTED")}
                      >
                        <FaTimes />
                        Reject
                      </button>
                    </div>
                  )}
                  {/* Reason/Comment */}
                  <div className={styles.leaveReason}>Reason: {req.reason}</div>
                </div>
              ))
            )}
            <div
              ref={leaveSentinelRef}
              className={styles.infiniteScrollSentinel}
              aria-hidden
            />
            {leaveLoadingMore && (
              <div className={styles.infiniteScrollLoading}>Loading more...</div>
            )}
            {!leaveLoading &&
              !leaveLoadingMore &&
              !hasMoreLeaves &&
              leaveRequests.length > 0 && (
                <div className={styles.infiniteScrollEnd}>End of requests</div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAttendancePanel;
